
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatTooltipCurrency, formatCurrency } from '../utils/formatters';
import { format } from 'date-fns';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

type DailyOrdersChartProps = {
  data: any[];
};

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const date = new Date(label);
    
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3">
        <p className="font-medium text-sm mb-2">
          {format(date, 'dd/MM/yyyy')}
        </p>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Total Orders (Count):</span>
            <span className="font-medium">{data.orders}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Total Order Value (Cart Value):</span>
            <span className="font-medium">{formatTooltipCurrency(data.revenue)}</span>
          </div>
          <div className="border-t pt-1 mt-1">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Pickup Orders:</span>
              <span className="font-medium">{data.pickupOrders || 0}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Delivery Orders:</span>
              <span className="font-medium">{data.deliveryOrders || 0}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Value of Pickup Orders:</span>
              <span className="font-medium">{formatTooltipCurrency(data.pickupRevenue || 0)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Value of Delivery Orders:</span>
              <span className="font-medium">{formatTooltipCurrency(data.deliveryRevenue || 0)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return null;
};

const DailyOrdersChart: React.FC<DailyOrdersChartProps> = ({ data }) => {
  const [view, setView] = useState<'count' | 'value'>('count');
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="space-y-1">
          <CardTitle>Daily Orders</CardTitle>
          <CardDescription>
            {view === 'count' ? 'Number of orders per day' : 'Order revenue per day'}
          </CardDescription>
        </div>
        <Tabs value={view} onValueChange={(value) => setView(value as 'count' | 'value')} className="h-7">
          <TabsList className="grid w-full grid-cols-2 h-7 p-0.5">
            <TabsTrigger value="count" className="text-xs h-6">Order Count</TabsTrigger>
            <TabsTrigger value="value" className="text-xs h-6">Order Value</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 10, right: 10, left: 40, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis 
                tickFormatter={view === 'value' ? (value) => formatCurrency(value) : undefined}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey={view === 'count' ? 'orders' : 'revenue'} 
                fill="#ffcc4b" 
                radius={[4, 4, 0, 0]} 
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default DailyOrdersChart;
